import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useEdgeCandidates } from "@/api/hooks/useCennik";
import { useZakazkaDetail } from "@/api/hooks/useZakazky";
import { useOknoZmenyHran } from "@/components/slot/ZmenaHranSlotuDialog";
import {
  DetectedEdgesSection,
  SlotEdgesByThicknessSection,
} from "@/components/slot/HranySlotu";
import type { EdgeCandidateRow, MaterialSlot } from "@/api/schemas";

export interface SlotHranyDialogProps {
  zakazkaId: string;
  slot: MaterialSlot | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Okno Hrany materiálu zákazky (KOLO 228, variant A stránky návrhov).
 *
 * User: *„musím hľadať automatické priradenie hrán, keď ťuknem na
 * materiál, a tam to je dole — lepšie by bolo, keby to malo tlačidlo"*.
 * Tabuľka hrán podľa hrúbky dielca je hneď hore, detegované hrany dekoru
 * pod ňou; každá zmena ide do dielcov cez okno so zoznamom (KOLO 222),
 * jeden krok späť.
 */
export function SlotHranyDialog({
  zakazkaId,
  slot,
  open,
  onOpenChange,
}: SlotHranyDialogProps): JSX.Element {
  const oknoHran = useOknoZmenyHran(slot?.id ?? "", slot?.slot_kod ?? "");
  // Projektová hrubá hrana určuje, ktorú hrubú appka vyberá
  const zakazkaQuery = useZakazkaDetail(zakazkaId);
  const hrubaHranaMm = zakazkaQuery.data?.data?.hruba_hrana_mm
    ? String(zakazkaQuery.data.data.hruba_hrana_mm)
    : undefined;
  const candidatesQuery = useEdgeCandidates(
    open ? (slot?.material_id ?? undefined) : undefined,
    hrubaHranaMm,
    slot?.id ?? undefined,
  );
  const candidates: EdgeCandidateRow[] =
    candidatesQuery.data?.data?.candidates ?? [];

  return (
    <Dialog open={open && !!slot} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Hrany {slot?.slot_kod}
            {slot?.material ? (
              <span className="font-normal text-muted-foreground">
                {" "}
                · {slot.material.nazov}
              </span>
            ) : null}
          </DialogTitle>
          <DialogDescription>
            Hrany, ktoré dostanú dielce s týmto materiálom podľa hrúbky. Klik
            na hranu nastaví inú — zmena ide aj do dielcov, ktoré idú podľa
            materiálu; ručne vybranú hranu dielca nemení.
          </DialogDescription>
        </DialogHeader>
        {slot ? (
          <div className="space-y-3">
            <SlotEdgesByThicknessSection
              slotId={slot.id}
              spustiZmenuHran={oknoHran.spusti}
            />
            {candidatesQuery.isLoading ? (
              <p className="text-xs text-muted-foreground">Hľadám hrany…</p>
            ) : candidates.length === 0 ? (
              <p className="text-xs text-warning">
                Pre dekor{" "}
                <span className="font-mono">
                  {candidatesQuery.data?.data?.material_dekor ?? "?"}
                </span>{" "}
                sa v cenníku nenašli žiadne hrany — klikni na bunku tabuľky
                a vyber hranu z celého cenníka.
              </p>
            ) : null}
            {/* Kompletný zoznam detegovaných hrán — zbalený detail */}
            <details className="rounded-md border">
              <summary className="cursor-pointer select-none px-2.5 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground">
                Detegované hrany ({candidates.length})
              </summary>
              <DetectedEdgesSection
                slotId={slot.id}
                candidates={candidates}
                spustiZmenuHran={oknoHran.spusti}
              />
            </details>
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Zavrieť
          </Button>
        </DialogFooter>
        {oknoHran.okno}
      </DialogContent>
    </Dialog>
  );
}
